import React, { useContext, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import moment from 'moment';

import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../constants/dateFormat';
import { EDIT_INVOICE } from '../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../services/auth';
import { formatDate } from '../../../../../services/date';
import { toBill } from '../../../../../services/financial';
import { Amount } from '../../shared/amount-view/Amount/Amount';
import { studyTypeOptionsMap } from '../../shared/FinanceFilters/MultiSelectStudyType';
import { finStudyTypeFilterOperator } from '../../shared/FinanseTableMUI/CustomFilterOperators';
import { DefaultFinanceCell } from '../../shared/FinanseTableMUI/DefaultFinanceCell';
import {
  dateValueGetter,
  formatDateWithoutUTC,
  prepareNameForCSV
} from '../../shared/FinanseTableMUI/FinanceTableServices';
import { resolveEventSuffix, resolveInvoiceSuffix, resolveVersion } from '../CreateInvoice/CreateInvoiceServices';
import { InvoiceContext } from '../NewInvoiceContext';

import PaymentStatus from './PaymentStatus/PaymentStatus';
import { TableItemMenuMUI } from './TableItemMenu/TableItemMenuMUI';
import {
  eventNumberComparator,
  getDaysOpen,
  invoiceNumberComparator,
  isCreditDebitCompleteStatus,
  isCreditMemoAppliedStatus,
  isOpenStatus,
  isPaidStatus,
  isPartialStatus,
  isPastDueStatus
} from './InvoiceTableService';
import { APPLIED, COMPLETE, OPEN, PAID, PARENT, PARTIAL, PAST_DUE } from './TableConstants';

function useInvoiceTableColumns() {
  return useMemo(() => {
    return [
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 125,
        sortComparator: eventNumberComparator,
        valueGetter: (value, row) => {
          if (row.adjustmentSequence === 0) {
            return resolveEventSuffix(row.eventNumber, row.invoiceType);
          }
          return resolveEventSuffix(`${row.eventNumber}.${row.adjustmentSequence}`, row.invoiceType);
        }
      },
      {
        field: 'name',
        headerName: 'Event Name',
        flex: 1,
        minWidth: 150,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'invoiceNumber',
        headerName: 'Invoice',
        flex: 1,
        minWidth: 120,
        sortComparator: invoiceNumberComparator,
        valueGetter: (value, row) => resolveInvoiceCellText(row),
        valueFormatter: (value, row) => resolveInvoiceCellExportText(row),
        getApplyQuickFilterFn: value => {
          const normalizedValue = value
            ?.toString()
            .trim()
            .toLowerCase();
          if (!normalizedValue) return null;
          return (fieldValue, row) =>
            resolveInvoiceCellText(row)
              .toLowerCase()
              .includes(normalizedValue);
        },
        renderCell: InvoiceNumberCell
      },
      {
        field: 'invoiceDate',
        headerName: 'Invoice Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueFormatter: value => formatDate.forCsv(value),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { invoiceDate } }) => (
          <div>{invoiceDate ? moment(invoiceDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        )
      },
      {
        field: 'revenueDate',
        headerName: 'Revenue Date',
        flex: 1,
        minWidth: 160,
        type: 'date',
        valueFormatter: formatDateWithoutUTC,
        valueGetter: dateValueGetter,
        renderCell: invoiceRevenueDateCell
      },
      {
        field: 'paymentStatus',
        headerName: 'Payment Status',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => resolvePaymentStatus(row),
        renderCell: StatusCell
      },
      {
        field: 'depositNumber',
        headerName: 'Deposit Number',
        flex: 1,
        minWidth: 180,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'postedDate',
        headerName: 'Date Posted',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueFormatter: value => (value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '-'),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row }) => (
          <div>{row.postedDate ? moment.utc(row.postedDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        )
      },
      {
        field: 'totalAmount',
        headerName: 'Total Amount',
        flex: 1,
        minWidth: 170,
        type: 'number',
        valueGetter: (value, row) => toBill(row?.totalAmount),
        renderCell: ({ row }) => <Amount coinsAmount={row?.totalAmount} showDollarSign />
      },
      {
        field: 'amountPaid',
        headerName: 'Amount Paid',
        flex: 1,
        minWidth: 160,
        type: 'number',
        valueGetter: (value, row) => toBill(row?.amountPaid),
        renderCell: ({ row }) => <Amount coinsAmount={row.amountPaid} showDollarSign />
      },
      {
        field: 'variance',
        headerName: 'Variance',
        flex: 1,
        minWidth: 130,
        type: 'number',
        valueGetter: (value, row) => toBill(row.amountPaid - row.totalAmount),
        renderCell: ({ row }) => (
          <Amount className="overflow-ellipsis" showDollarSign coinsAmount={row.amountPaid - row.totalAmount} />
        )
      },
      {
        field: 'actions',
        type: 'actions',
        resizable: false,
        width: 50,
        renderCell: ({ row }) => <TableItemMenuMUI row={row} />
      },
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1,
        minWidth: 110,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'finStudyType',
        headerName: 'Study Type',
        flex: 1,
        minWidth: 145,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        valueGetter: value => studyTypeOptionsMap.find(option => option.id === value)?.name || value,
        filterOperators: finStudyTypeFilterOperator
      },
      {
        field: 'pcnName',
        headerName: 'PCN',
        flex: 1,
        minWidth: 100,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1,
        minWidth: 100,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'siteNumber',
        headerName: 'Site Number',
        flex: 1,
        minWidth: 150,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => (value != null ? `[${value}]` : '')
      },
      {
        field: 'clientName',
        headerName: 'Client',
        flex: 1,
        minWidth: 115,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'eventDate',
        headerName: 'Event Date',
        flex: 1,
        minWidth: 150,
        type: 'date',
        valueFormatter: value => formatDate.forCsv(value),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row }) => {
          const content = moment(row.eventDate).format(DD_SLASH_MMM_SLASH_YYYY);
          return <span title={content}>{content}</span>;
        }
      },
      {
        field: 'daysOpen',
        headerName: 'Days Open',
        flex: 1,
        minWidth: 140,
        valueGetter: (value, row) =>
          getDaysOpen({ ...row, invoiceDate: row.invoiceDate ? moment(row.invoiceDate) : moment(0) }),
        renderCell: DefaultFinanceCell
      },
      {
        field: 'budgetEventType',
        headerName: 'Budget Event Type',
        flex: 1,
        minWidth: 190,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'epochName',
        headerName: 'Epoch',
        flex: 1,
        minWidth: 110,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'itemGroupName',
        headerName: 'Item Group',
        flex: 1,
        minWidth: 140,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'adjustmentReason',
        headerName: 'Adjustment Memo',
        flex: 1,
        minWidth: 190,
        renderCell: ({ value }) => (
          <Tooltip placement="top" title={<div>{value || '—'}</div>}>
            <span className="table-cell-tooltip">{value || '—'}</span>
          </Tooltip>
        )
      },
      {
        field: 'investigator',
        headerName: 'Investigator',
        flex: 1,
        minWidth: 150,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'patientId',
        headerName: 'Patient ID',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'subjectId',
        headerName: 'Subject ID',
        flex: 1,
        minWidth: 145,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => (value != null ? `[${value}]` : '')
      },
      {
        field: 'nonInvoiceable',
        headerName: 'Non-Invoiceable',
        minWidth: 180,
        valueGetter: value => (value ? 'Yes' : 'No')
      },
      {
        field: 'expenseVariableComment',
        headerName: 'Expense Comments',
        flex: 1,
        minWidth: 150,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      }
    ];
  }, []);
}

const resolvePaymentStatus = row => {
  if (isCreditMemoAppliedStatus(row)) return APPLIED;
  else if (isCreditDebitCompleteStatus(row)) return COMPLETE;
  else if (isOpenStatus(row)) return OPEN;
  else if (isPastDueStatus(row)) return PAST_DUE;
  else if (isPartialStatus(row)) return PARTIAL;
  else if (isPaidStatus(row)) return PAID;
  return '';
};

const InvoiceNumberCell = ({ row }) => {
  const { invoiceNumber, invoiceFileId, invoiceType, oldInvoice, invoiceFileCreated, issuedWithParentInvoice } = row;
  const { loadInvoice, getDataForAutomaticallyCreatedInvoice } = useContext(InvoiceContext);
  const textContent = resolveInvoiceCellText(row) || '—';
  const canEditInvoice = userHasAccessTo(EDIT_INVOICE);
  const isGenerateInvoice = issuedWithParentInvoice === false;
  const canOpenInvoice = !!invoiceFileId && (!isGenerateInvoice || canEditInvoice);

  return canOpenInvoice ? (
    <Button
      type="text"
      onClick={() => {
        if (invoiceFileId) {
          const openInEditMode = issuedWithParentInvoice === false;
          if (!invoiceFileCreated) {
            getDataForAutomaticallyCreatedInvoice(invoiceNumber, invoiceType, invoiceFileId, openInEditMode);
          } else {
            const type = oldInvoice || issuedWithParentInvoice !== null ? PARENT : invoiceType;
            loadInvoice(invoiceNumber, invoiceFileId, type, { openInEditMode });
          }
        }
      }}
      sx={{
        minWidth: 'min-content',
        padding: '0',
        color: '#2465C6',
        fontWeight: '400',
        textTransform: 'none'
      }}
    >
      {textContent}
    </Button>
  ) : (
    <div>{textContent}</div>
  );
};

const resolveInvoiceCellText = row => {
  const baseText = resolveInvoiceCellBaseText(row);
  return baseText && row.issuedWithParentInvoice === false ? `${baseText} Generate` : baseText;
};

const resolveInvoiceCellExportText = row => {
  if (row?.issuedWithParentInvoice === false) {
    return '';
  }
  return resolveInvoiceCellBaseText(row);
};

const resolveInvoiceCellBaseText = ({ displayNumber, invoiceType, issuedWithParentInvoice, currentVersion }) =>
  displayNumber
    ? `${resolveInvoiceSuffix(displayNumber, invoiceType, issuedWithParentInvoice)}${resolveVersion(currentVersion)}`
    : '';

const StatusCell = ({ row, value }) => (
  <div className="cell-with-status">
    <div>{value}</div>
    <PaymentStatus status={value} hasInvoiceNumber={!!row?.invoiceNumber} />
  </div>
);

export const invoiceRevenueDateCell = ({ row: { revenueDate } }) => (
  <div>{revenueDate ? moment(revenueDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
);

export default useInvoiceTableColumns;
